"use client";

import { usePathname } from "next/navigation";
import { useEffect, useRef } from "react";
import {
  buildMarketingAnalyticsConfig,
  extractAttribution,
  marketingEvents,
  scrollDepthMilestones,
} from "@/core/observability/marketing-analytics";

interface MarketingPostHog {
  init: (key: string, options: Record<string, unknown>) => void;
  capture: (event: string, properties?: Record<string, unknown>) => void;
  register?: (properties: Record<string, unknown>) => void;
}

// window.posthog is declared (with a narrower app-focused shape) in
// PostHogProvider.tsx; the loaded array.js client also exposes capture and
// register, so widen through unknown here.
function getPostHog(): MarketingPostHog | undefined {
  return (window as { posthog?: unknown }).posthog as MarketingPostHog | undefined;
}

export function MarketingAnalytics() {
  const pathname = usePathname();
  const initialized = useRef(false);
  const lastTrackedPath = useRef<string | null>(null);

  useEffect(() => {
    if (privateCommercePage() || initialized.current) {
      return;
    }

    const config = buildMarketingAnalyticsConfig({
      marketingKey: process.env.NEXT_PUBLIC_MARKETING_POSTHOG_KEY,
      appKey: process.env.NEXT_PUBLIC_POSTHOG_KEY,
      host: process.env.NEXT_PUBLIC_POSTHOG_HOST,
      doNotTrack: navigator.doNotTrack === "1",
    });

    if (!config.enabled) {
      return;
    }

    initialized.current = true;

    const init = () => {
      const posthog = getPostHog();
      if (!posthog) {
        return;
      }
      posthog.init(config.key, {
        api_host: config.host,
        capture_pageview: false,
        disable_session_recording: true,
        person_profiles: "identified_only",
        autocapture: false,
        before_send: (event: { properties?: Record<string, unknown> }) => {
          if (privateCommercePage()) return null;
          if (event.properties) {
            event.properties.$current_url = window.location.origin + window.location.pathname;
            delete event.properties.$referrer;
          }
          return event;
        },
      });

      const attribution = extractAttribution(window.location.search);
      if (Object.keys(attribution).length > 0) {
        posthog.register?.(attribution);
      }

      capturePageView();
    };

    if (getPostHog()) {
      init();
      return;
    }

    const script = document.createElement("script");
    script.async = true;
    script.src = `${config.host.replace(/\/$/, "")}/static/array.js`;
    script.onload = init;
    document.head.appendChild(script);
  }, []);

  // SPA navigations: capture a pageview per pathname change.
  useEffect(() => {
    if (!initialized.current || lastTrackedPath.current === pathname) {
      return;
    }
    if (lastTrackedPath.current !== null) {
      capturePageView();
    }
    lastTrackedPath.current = pathname;
  }, [pathname]);

  // Delegated CTA tracking: any element with data-marketing-event captures on
  // click, so server components stay server components.
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (privateCommercePage()) return;
      const target = (event.target as HTMLElement | null)?.closest<HTMLElement>("[data-marketing-event]");
      if (!target) {
        return;
      }
      const eventName = target.dataset.marketingEvent;
      if (!eventName) {
        return;
      }
      getPostHog()?.capture(eventName, {
        href: target.getAttribute("href") ?? undefined,
        cta_location: target.dataset.marketingLocation ?? undefined,
        page_path: window.location.pathname,
      });
    };
    document.addEventListener("click", onClick, { capture: true });
    return () => document.removeEventListener("click", onClick, { capture: true });
  }, []);

  // Scroll depth milestones per page.
  useEffect(() => {
    const reached = new Set<number>();
    const onScroll = () => {
      if (privateCommercePage()) return;
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable <= 0) {
        return;
      }
      const percent = Math.round((window.scrollY / scrollable) * 100);
      for (const milestone of scrollDepthMilestones) {
        if (percent >= milestone && !reached.has(milestone)) {
          reached.add(milestone);
          getPostHog()?.capture(marketingEvents.scrollDepth, {
            depth: milestone,
            page_path: window.location.pathname,
          });
        }
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [pathname]);

  return null;
}

function privateCommercePage() { return /^\/(purchase|apply|email-preferences)(\/|$)/.test(window.location.pathname); }

function capturePageView() {
  if (privateCommercePage()) return;
  getPostHog()?.capture(marketingEvents.pageView, {
    $current_url: window.location.origin + window.location.pathname,
  });
}
